import { DateTime } from 'luxon';
import { useTheme } from '@emotion/react';
import { H2 } from '../styled/typography';
import { Image } from '../picture/picture';
import CommentSVG from '../../public/img/comment.svg';
import ArrowCornerSVG from '../../public/img/arrow-corner.svg';
import {
  DEVICE_SCREENS_MAX_STRING,
  DEVICE_SCREENS_MIN_STRING,
} from '../../lib/consts';
import { BLOG_DOMAIN } from '../../lib/api/apiUrl';
import { Icon } from '../icon/icon';
import FavoriteButtonWrapper from '../favorite/FavoriteButtonWrapper';
import FavoriteButton from '../favorite/FavoriteButton';
import { FavoriteKinds } from '../../models/favorite';
import { Material, Person, Place } from '../../models';

type IReadMoreProps = {
  materials: Material[];
  person?: Person | null;
  place?: Place | null;
};

export function ReadMore({ materials, person, place }: IReadMoreProps) {
  const theme = useTheme();
  const getDate = (time: string) =>
    DateTime.fromISO(time).setLocale('ru').toFormat('dd MMMM yyyy');

  const getItemLink = (item: Material) =>
    `${BLOG_DOMAIN}/blog/${item.blog_categories[0].slug}/${item.slug}`;

  const getBlockLink = () => {
    let link = `${BLOG_DOMAIN}/blog/`;

    if (person) {
      link += `search?person=${person.id}`;
    } else if (place) {
      link += `search?place=${place.id}`;
    }

    return link;
  };

  return (
    <div
      css={{
        margin: '0 -11px',
        [DEVICE_SCREENS_MIN_STRING.xxl]: {
          margin: '0 7px',
        },
        [DEVICE_SCREENS_MAX_STRING.xl]: {
          margin: '0px -10px',
        },
        [DEVICE_SCREENS_MAX_STRING.md]: {
          margin: '0px 8px',
        },
      }}
    >
      <div
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <H2
          css={{
            fontSize: 26,
            fontWeight: 700,
            color: '#282828',
            fontFamily: 'Montserrat',
            [DEVICE_SCREENS_MAX_STRING.sm]: {
              fontSize: 18,
            },
          }}
        >
          Читайте также
        </H2>

        <a
          css={{
            display: 'flex',
            justifyContent: 'center',
            fontFamily: 'Montserrat',
            fontSize: '15px',
            fontWeight: 600,
            textDecoration: 'none',
            color: '#f44336',
            '&:hover': {
              color: 'rgba(244, 67, 54, 0.85)',
            },
            [DEVICE_SCREENS_MAX_STRING.md]: {
              display: 'none',
            },
          }}
          href={getBlockLink()}
          target='_blank'
          rel='noreferrer'
        >
          Смотреть все
          <ArrowCornerSVG
            css={{
              width: '20px',
              height: '20px',
              fill: 'rgba(244, 67, 54, 0.85)',
            }}
          />
        </a>
      </div>

      <div
        css={{
          display: 'grid',
          gap: 16,
          marginTop: 24,
          marginBottom: 16,
          gridTemplateColumns: 'repeat(4, 1fr)',
          [DEVICE_SCREENS_MAX_STRING.xl]: {
            gridTemplateColumns: 'repeat(3, 1fr)',
          },
          [DEVICE_SCREENS_MAX_STRING.md]: {
            gridTemplateColumns: '1fr',
          },
        }}
      >
        {materials.slice(0, 4).map((item) => (
          <div
            key={`News${item.title}`}
            css={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              position: 'relative',
              [DEVICE_SCREENS_MAX_STRING.md]: {},
            }}
          >
            <a
              href={getItemLink(item)}
              target='_blank'
              rel='noreferrer'
              css={{
                width: '100%',
                height: 200,
                paddingBottom: '53%',
                position: 'relative',
              }}
            >
              <Image
                image={item.animated_cover ?? item.photo ?? ''}
                alt={item.title}
                css={{
                  backgroundColor: theme.colors.white,
                  img: {
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: 'hidden',
                    objectFit: 'cover',
                    margin: 'auto',
                  },
                }}
              />
            </a>

            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flex: 1,
                marginTop: 12,
              }}
            >
              <div>
                <div
                  css={{
                    display: 'flex',
                    color: '#7D7B7A',
                    fontSize: 14,
                    lineHeight: 1.4,
                    fontWeight: 400,
                    gap: 4,
                    marginBottom: 8,
                  }}
                >
                  {!!item.author && <span>{item.author}</span>}|
                  <span>{getDate(item.published_at)}</span>
                </div>

                <a href={getItemLink(item)} target='_blank' rel='noreferrer'>
                  <H2
                    css={{
                      color: '#282828',
                      fontSize: 14,
                      fontWeight: 700,
                      fontFamily: '"Golos", Arial, serif',
                    }}
                  >
                    {item.title}
                  </H2>
                </a>
              </div>

              <div
                css={{
                  display: 'flex',
                  gap: 20,
                  marginTop: 12,
                  fontWeight: 400,
                  color: '#7D7B7A',
                }}
              >
                <span
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    fontSize: 12,
                    fontWeight: 400,
                  }}
                >
                  <svg
                    css={{
                      width: 20,
                      height: 20,
                      fill: '#7D7B7A',
                      stroke: '#fff',
                    }}
                  >
                    <use xlinkHref='#eye' />
                  </svg>
                  {item.views}
                </span>
                <span
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    svg: { fill: '#7d7b7a' },
                    fontSize: 12,
                    fontWeight: 400,
                  }}
                >
                  <Icon iconId='star' />
                  {item.avg_rating}
                </span>
                <span
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    fontSize: 12,
                    fontWeight: 400,
                  }}
                >
                  <CommentSVG
                    css={{
                      width: 25,
                      height: 25,
                      fill: '#7D7B7A',
                    }}
                  />
                  {item.active_comments_count}
                </span>
              </div>

              <FavoriteButtonWrapper fromMaterial>
                <FavoriteButton
                  kind={FavoriteKinds.material}
                  id={item.id}
                  size='small'
                  variant='red-outline'
                  actionShip
                />
              </FavoriteButtonWrapper>
            </div>
          </div>
        ))}
      </div>

      <div
        css={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24,
        }}
      >
        <button
          type='button'
          css={{
            width: 220,
            height: 40,
            borderRadius: 19,
            fontFamily: 'Montserrat',
            fontWeight: 600,
            fontSize: '15px',
            color: '#fff',
            boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.25)',
            backgroundColor: '#ff4a2e',
            backgroundImage:
              'linear-gradient(120deg, #ff4a2e 0%, #de2839 100%)',
            backgroundSize: '500% 500%',
            animation: 'move-background 60s linear infinite',
            transition: 'all 0.2s ease, box-shadow 0.4s ease',
            '&:hover': {
              boxShadow: '0px 0px 10px 0px #de2839',
            },
          }}
          onClick={() => {
            window.open(getBlockLink());
          }}
        >
          Показать еще
        </button>

        <a
          css={{
            display: 'none',
            justifyContent: 'center',
            fontFamily: 'Montserrat',
            fontSize: '15px',
            fontWeight: 600,
            textDecoration: 'none',
            color: '#f44336',
            '&:hover': {
              color: 'rgba(244, 67, 54, 0.85)',
            },
            [DEVICE_SCREENS_MAX_STRING.md]: {
              display: 'flex',
            },
          }}
          href={getBlockLink()}
          target='_blank'
          rel='noreferrer'
        >
          Смотреть все
          <ArrowCornerSVG
            css={{
              width: '20px',
              height: '20px',
              fill: 'rgba(244, 67, 54, 0.85)',
            }}
          />
        </a>
      </div>
    </div>
  );
}
